import React, {useEffect, useMemo} from 'react'
import {useLocation, useNavigate, useParams} from "react-router-dom";
import Header from "../../ThemeComponents/Header";
import Footer from "../../ThemeComponents/Footer";
import Loader from "../../../CommonComponents/Loader";
import {searchExternalRentVehicleAPI} from "../../network/publicCalls/GETSearchExternalRentVehicle";
import {appendPublicWrapperBaseToSearch} from "../../config/publicWrapperBase";

const DATE_DD_MM_YYYY_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/
const DATE_YYYY_MM_DD_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/
const TIME_HH_MM_REGEX = /^(\d{1,2}):(\d{2})$/

const isValidDateParts = ({year, month, day}) => {
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return false
    if (month < 1 || month > 12 || day < 1 || day > 31) return false
    const candidate = new Date(year, month - 1, day)
    return (
        candidate.getFullYear() === year &&
        candidate.getMonth() === month - 1 &&
        candidate.getDate() === day
    )
}

const normalizeDatePart = (value) => {
    const rawValue = String(value || "").trim()
    if (!rawValue) return ""

    const ddmmyyyyMatch = rawValue.match(DATE_DD_MM_YYYY_REGEX)
    if (ddmmyyyyMatch) {
        const [, day, month, year] = ddmmyyyyMatch
        if (!isValidDateParts({year: Number(year), month: Number(month), day: Number(day)})) return ""
        return `${year}-${month}-${day}`
    }

    if (DATE_YYYY_MM_DD_REGEX.test(rawValue)) {
        const [, year, month, day] = rawValue.match(DATE_YYYY_MM_DD_REGEX)
        if (!isValidDateParts({year: Number(year), month: Number(month), day: Number(day)})) return ""
        return rawValue
    }

    return ""
}

const normalizeTimePart = (value) => {
    const rawValue = String(value || "").trim()
    if (!rawValue) return ""

    const timeMatch = rawValue.match(TIME_HH_MM_REGEX)
    if (!timeMatch) return ""

    const hour = Number(timeMatch[1])
    const minute = Number(timeMatch[2])
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return ""
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return ""

    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
}

const normalizeDateTimeValue = (value) => {
    const rawValue = String(value || "").trim()
    if (!rawValue) return ""

    const parsedDate = new Date(rawValue)
    if (Number.isNaN(parsedDate.getTime())) return ""

    const year = parsedDate.getFullYear()
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0")
    const day = String(parsedDate.getDate()).padStart(2, "0")
    const hours = String(parsedDate.getHours()).padStart(2, "0")
    const minutes = String(parsedDate.getMinutes()).padStart(2, "0")
    return `${year}-${month}-${day}T${hours}:${minutes}`
}

const buildDateTimeValue = ({dateValue, timeValue}) => {
    const datePart = normalizeDatePart(dateValue)
    const timePart = normalizeTimePart(timeValue)
    if (!datePart || !timePart) return ""
    return `${datePart}T${timePart}`
}

const buildReservationSearchFromExternalParams = (search) => {
    const queryParams = new URLSearchParams(search)
    const from = buildDateTimeValue({
        dateValue: queryParams.get("dateFrom"),
        timeValue: queryParams.get("timeFrom")
    }) || normalizeDateTimeValue(queryParams.get("from"))
    const to = buildDateTimeValue({
        dateValue: queryParams.get("dateTo"),
        timeValue: queryParams.get("timeTo")
    }) || normalizeDateTimeValue(queryParams.get("to"))
    const expectedKm = String(queryParams.get("km") || queryParams.get("expectedKm") || "").trim()

    const reservationQuery = new URLSearchParams()
    if (from) reservationQuery.set("from", from)
    if (to) reservationQuery.set("to", to)
    if (expectedKm) reservationQuery.set("expectedKm", expectedKm)

    return appendPublicWrapperBaseToSearch(reservationQuery, search).toString()
}

const normalizeRedirectTarget = (search) => {
    const queryParams = new URLSearchParams(search)
    const rawRedirectTo = String(queryParams.get("redirectTo") || "").trim()
    if (!rawRedirectTo || typeof window === "undefined") return ""

    try {
        const redirectUrl = new URL(rawRedirectTo, window.location.origin)
        if (!["http:", "https:"].includes(redirectUrl.protocol)) return ""
        return redirectUrl.toString()
    } catch (_error) {
        return ""
    }
}

const ExternalRentRedirectPage = () => {
    const {subsidiaryId, vehicleExternalId} = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    const reservationSearch = useMemo(
        () => buildReservationSearchFromExternalParams(location.search),
        [location.search]
    )
    const redirectTo = useMemo(
        () => normalizeRedirectTarget(location.search),
        [location.search]
    )

    useEffect(() => {
        const redirectToFallback = () => {
            if (redirectTo && typeof window !== "undefined") {
                window.location.assign(redirectTo)
                return
            }
            navigate("/", {replace: true})
        }

        if (!subsidiaryId || !vehicleExternalId) {
            redirectToFallback()
            return undefined
        }

        let isMounted = true

        ;(async () => {
            try {
                const vehicleSearchResponse = await searchExternalRentVehicleAPI({
                    subsidiaryId,
                    vehicleExternalId
                })

                if (!isMounted) return

                const vehicleId = Number(vehicleSearchResponse?.vehicle_id)
                const branchId = Number(vehicleSearchResponse?.branch_id)
                if (!Number.isFinite(vehicleId) || !Number.isFinite(branchId)) {
                    redirectToFallback()
                    return
                }

                navigate({
                    pathname: `/reservation/${vehicleId}/${branchId}`,
                    search: reservationSearch ? `?${reservationSearch}` : ""
                }, {replace: true})
            } catch (error) {
                if (!isMounted) return
                if (Number(error?.status) === 404) {
                    redirectToFallback()
                    return
                }

                console.error("External rent vehicle lookup failed", error)
                redirectToFallback()
            }
        })()

        return () => {
            isMounted = false
        }
    }, [subsidiaryId, vehicleExternalId, redirectTo, reservationSearch, navigate])

    return (
        <>
            <Header/>
            <div style={{height: '70px'}}></div>
            <div className="container mt-5 mb-5">
                <div className="front-location-loader">
                    <div className="front-location-loader__text" style={{minHeight: 'unset'}}>
                        Een moment geduld, we zoeken je voertuig en openen direct de reservering.
                    </div>
                    <div className="text-center">
                        <Loader/>
                    </div>
                </div>
            </div>
            <Footer/>
        </>
    )
}

export default ExternalRentRedirectPage
